import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  Download,
  RefreshCw,
  DollarSign,
  Calendar,
  Users,
  Edit3
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getPayrollRun,
  approvePayrollRun,
  markPayrollPaid,
  processPayrollRun,
  reversePayrollRun,
  deletePayrollRun
} from '../../../api/payroll';
import BlurredValue from '../../../components/common/BlurredValue';
import PayslipDetailModal from '../../../components/payroll/PayslipDetailModal';
import EditPayrollRunModal from '../../../components/payroll/EditPayrollRunModal';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import ConfirmDeletePayrollModal from '../../../components/payroll/ConfirmDeletePayrollModal';

const WholesalePayrollRunDetail = () => {
  const { runId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = ['super_admin', 'system_admin', 'wholesale_admin'].includes(user?.role);

  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [paymentRef, setPaymentRef] = useState('');
  const [selectedPayslipId, setSelectedPayslipId] = useState(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPayrollRun(runId);
      setRun(data);
    } catch (error) {
      toast.error('Failed to load payroll run');
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleProcess = async () => {
    if (!confirm('Process this payroll run?')) return;
    try {
      await processPayrollRun(runId);
      toast.success('Payroll processed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to process');
    }
  };

  const handleApprove = async () => {
    if (!confirm('Approve this payroll run?')) return;
    try {
      await approvePayrollRun(runId);
      toast.success('Payroll approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const handleMarkPaid = async () => {
    try {
      await markPayrollPaid(runId, paymentRef);
      toast.success('Marked as paid');
      setShowPayModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to mark as paid');
    }
  };

  const handleReverse = async () => {
    if (
      !confirm(
        'Are you sure you want to recall/reverse this payroll run? This will reverse all payments, loan repayments, and GL/Cash Flow entries, and return the run to draft status.'
      )
    )
      return;
    try {
      await reversePayrollRun(runId);
      toast.success('Payroll run successfully recalled/reversed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reverse payroll run');
    }
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDeleteRun = async () => {
    setDeleting(true);
    try {
      await deletePayrollRun(runId);
      toast.success('Payroll run deleted successfully');
      setShowDeleteModal(false);
      navigate('/wholesale/payroll/runs');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete payroll run');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      pending_approval: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      paid: 'bg-emerald-100 text-emerald-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return <LoadingSpinner className="min-h-screen" />;
  }

  if (!run) {
    return (
      <div className="min-h-screen bg-secondary-offWhite flex items-center justify-center">
        <p className="text-gray-500">Payroll run not found</p>
      </div>
    );
  }

  const totalNhf =
    run.payslips?.reduce((sum, p) => sum + (parseFloat(p.nhf_deduction) || 0), 0) || 0;

  const totalLoans =
    run.payslips?.reduce((sum, p) => sum + (parseFloat(p.loan_deduction) || 0), 0) || 0;

  return (
    <div className="min-h-screen bg-secondary-offWhite p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/wholesale/payroll/runs')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-xl sm:text-2xl font-bold text-primary-black">
                    {run.description || run.run_number}
                  </h1>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(run.status)}`}
                  >
                    {(run.status || '').replace('_', ' ')}
                  </span>
                </div>
                <p className="text-sm text-gray-500">
                  {new Date(run.pay_period_start).toLocaleDateString()} -{' '}
                  {new Date(run.pay_period_end).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowEditModal(true)}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-1.5 shadow-sm"
              >
                <Edit3 className="w-4 h-4" />
                Edit Run
              </button>
              {run.status === 'draft' && (
                <button
                  onClick={handleProcess}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Process Payroll
                </button>
              )}
              {(run.status === 'pending_approval' || run.status === 'approved') && (
                <button
                  onClick={handleProcess}
                  disabled={loading}
                  className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Recalculate
                </button>
              )}
              {run.status === 'pending_approval' && isSuperAdmin && (
                <button
                  onClick={handleApprove}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Approve
                </button>
              )}
              {run.status === 'approved' && isSuperAdmin && (
                <button
                  onClick={() => setShowPayModal(true)}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                >
                  Mark as Paid
                </button>
              )}
              {run.status !== 'draft' && (
                <button
                  onClick={handleReverse}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                >
                  Recall/Reverse
                </button>
              )}
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete Run
              </button>
              <button onClick={fetchData} className="p-2 border rounded-lg hover:bg-gray-50">
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <Users className="w-4 h-4" />
              <span className="text-sm">Employees</span>
            </div>
            <p className="text-2xl font-bold">{run.employee_count || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <DollarSign className="w-4 h-4" />
              <span className="text-sm">Total Gross</span>
            </div>
            <p className="text-xl font-bold">
              <BlurredValue value={formatCurrency(run.total_gross)} />
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <DollarSign className="w-4 h-4" />
              <span className="text-sm">Total Deductions</span>
            </div>
            <p className="text-xl font-bold text-red-600">
              <BlurredValue value={formatCurrency(run.total_deductions)} />
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <DollarSign className="w-4 h-4" />
              <span className="text-sm">Net Pay</span>
            </div>
            <p className="text-xl font-bold text-green-600">
              <BlurredValue value={formatCurrency(run.total_net)} />
            </p>
          </div>
        </div>

        {/* Deductions Breakdown */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-600 mb-1">PAYE Tax</p>
            <p className="text-lg font-semibold">
              <BlurredValue value={formatCurrency(run.total_paye)} />
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-600 mb-1">NHF</p>
            <p className="text-lg font-semibold">
              <BlurredValue value={formatCurrency(totalNhf)} />
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-600 mb-1">Employee Pension</p>
            <p className="text-lg font-semibold">
              <BlurredValue value={formatCurrency(run.total_pension_employee)} />
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-600 mb-1">Loan Deductions</p>
            <p className="text-lg font-semibold text-blue-600">
              <BlurredValue value={formatCurrency(totalLoans)} />
            </p>
          </div>
        </div>

        {/* Payslips Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Payslips ({run.payslips?.length || 0})
            </h3>
          </div>

          {run.payslips?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Employee</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Gross</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">NHF</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">PAYE</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Pension</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Net Pay</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {run.payslips.map((payslip) => (
                    <tr
                      key={payslip.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedPayslipId(payslip.id)}
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium">{payslip.employee_name}</p>
                        <p className="text-xs text-gray-500">{payslip.payslip_number}</p>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <BlurredValue value={formatCurrency(payslip.gross_earnings)} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <BlurredValue value={formatCurrency(payslip.nhf_deduction)} />
                      </td>
                      <td className="px-4 py-3 text-right text-red-600">
                        <BlurredValue value={formatCurrency(payslip.paye_tax)} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <BlurredValue value={formatCurrency(payslip.pension_employee)} />
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-green-600">
                        <BlurredValue value={formatCurrency(payslip.net_pay)} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            payslip.payment_status === 'paid'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}
                        >
                          {payslip.payment_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>No payslips generated yet</p>
              {run.status === 'draft' && (
                <button
                  onClick={handleProcess}
                  className="mt-4 px-4 py-2 bg-accent-gold text-primary-black rounded-lg"
                >
                  Process Payroll
                </button>
              )}
            </div>
          )}
        </div>

        {/* Pay Modal */}
        {showPayModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-sm">
              <div className="p-4 border-b">
                <h2 className="text-lg font-semibold">Mark as Paid</h2>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Payment Reference</label>
                  <input
                    type="text"
                    value={paymentRef}
                    onChange={(e) => setPaymentRef(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., TRF-123456"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowPayModal(false)}
                    className="px-4 py-2 border rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleMarkPaid}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payslip Detail Modal */}
        <PayslipDetailModal
          payslipId={selectedPayslipId}
          isOpen={!!selectedPayslipId}
          onClose={() => setSelectedPayslipId(null)}
        />

        {/* Edit Payroll Run Modal */}
        <EditPayrollRunModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          run={run}
          onSuccess={fetchData}
          module="wholesale"
        />

        {/* Delete Confirmation Modal */}
        <ConfirmDeletePayrollModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={confirmDeleteRun}
          run={run}
          loading={deleting}
        />
      </div>
    </div>
  );
};

export default WholesalePayrollRunDetail;
